import Stepper from "@/components/common/Stepper";
import Title from "@/components/common/Title";
import { studyByIdQueryOptions } from "@/features/studies/api/selectStudies";
import { StudySummaryCard } from "@/features/studies/components/StudySummaryCard";
import { studyInviteSearchSchema } from "@/features/studies/utils/studyInviteSearch";
import { getStudyInviteSteps } from "@/features/studies/utils/studyInviteSteps";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { useStudiesCopy } from "@/features/i18n/studiesCopy";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { QrCode } from "lucide-react";

function StudyInviteHubPage() {
    const { studyId } = Route.useParams();
    const navigate = useNavigate();
    const inviteCopy = useInviteCopy();
    const studiesCopy = useStudiesCopy();

    const { data: study, isLoading } = useQuery(studyByIdQueryOptions(studyId));

    useNav([
        { route: { to: "/studies" }, label: studiesCopy.studies },
        {
            route: { to: "/studies/$studyId", params: { studyId } },
            label: study?.study_name ?? studiesCopy.studies,
        },
        {
            route: { to: "/studies/$studyId/invite", params: { studyId } },
            label: studiesCopy.studyInvite,
        },
    ]);

    if (isLoading) {
        return <p className="text-muted-foreground text-sm">{studiesCopy.loadingStudy}</p>;
    }

    if (!study) {
        return (
            <section className="space-y-4">
                <Title words={studiesCopy.studyNotFound} />
                <ui.Button type="button" variant="outline" onClick={() => void navigate({ to: "/studies" })}>
                    {studiesCopy.backToStudies}
                </ui.Button>
            </section>
        );
    }

    return (
        <section className="space-y-6 py-4 md:py-6 w-full min-w-0">
            <div className="space-y-4">
                <Stepper steps={getStudyInviteSteps(studyId, "hub", studiesCopy)} />
            </div>

            <StudySummaryCard study={study} />

            <div className="flex md:flex-row flex-col gap-4">
                <ui.Button
                    type="button"
                    className="gap-2"
                    data-testid="study-invite-by-qr"
                    onClick={() =>
                        void navigate({
                            to: "/studies/$studyId/invite/qr",
                            params: { studyId },
                        })
                    }
                >
                    <QrCode className="w-4 h-4" />
                    {inviteCopy.shareQrCode}
                </ui.Button>
            </div>
        </section>
    );
}

export const Route = createFileRoute("/_app/studies/$studyId/invite/")({
    component: StudyInviteHubPage,
    validateSearch: (search) => studyInviteSearchSchema.parse(search),
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(studyByIdQueryOptions(params.studyId));
    },
});
